import React from 'react';
import { AuthLogo } from './AuthLogo';

/**
 * Loading animation variant types
 */
type LoadingVariant = 'default' | 'pulse' | 'spinner';

/**
 * Component size types
 */
type ComponentSize = 'sm' | 'md' | 'lg';

/**
 * Props interface for loading state components
 */
interface LoadingStateProps {
  readonly message?: string;
  readonly submessage?: string; // Secondary message for additional context
  readonly progress?: number;
  readonly variant?: LoadingVariant;
  readonly size?: ComponentSize;
}

/**
 * Animated Spinner Component
 */
const AnimatedSpinner: React.FC<{ size?: ComponentSize }> = ({
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`${sizeClasses[size]} animate-spin`}>
      <div className="w-full h-full border-4 border-True-Blue border-t-transparent rounded-full"></div>
    </div>
  );
};

/**
 * Shared Animated Dots Component
 * Consolidates duplicate animated dots patterns used across authentication components
 */
export const AnimatedDots: React.FC<{
  variant?: 'pulse' | 'bounce';
  size?: 'sm' | 'md';
}> = ({ variant = 'pulse', size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';
  const animationClass =
    variant === 'bounce' ? 'animate-bounce' : 'animate-pulse';
  const spacing = size === 'sm' ? 'space-x-1' : 'space-x-2';

  return (
    <div className={`flex justify-center ${spacing}`} aria-label="Loading">
      <div
        className={`${sizeClasses} bg-True-Blue rounded-full ${animationClass}`}
      ></div>
      <div
        className={`${sizeClasses} bg-True-Blue rounded-full ${animationClass}`}
        style={{ animationDelay: variant === 'bounce' ? '0.1s' : '0.2s' }}
      ></div>
      <div
        className={`${sizeClasses} bg-True-Blue rounded-full ${animationClass}`}
        style={{ animationDelay: variant === 'bounce' ? '0.2s' : '0.4s' }}
      ></div>
    </div>
  );
};

/**
 * Progress Bar Component
 */
const ProgressBar: React.FC<{ progress: number }> = ({ progress }) => {
  const normalizedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
      <progress
        className="sr-only"
        value={normalizedProgress}
        max={100}
        aria-label={`Loading progress: ${normalizedProgress}%`}
      >
        {normalizedProgress}%
      </progress>
      <div
        className="bg-gradient-to-r from-True-Blue to-Blue-Text h-2 rounded-full transition-all duration-500 ease-out"
        style={{ width: `${normalizedProgress}%` }}
        aria-hidden="true"
      />
    </div>
  );
};

/**
 * Branded Logo Animation using the shared AuthLogo component
 */
const BrandedLogoAnimation: React.FC<{ size?: ComponentSize }> = ({
  size = 'md',
}) => {
  let logoSize: 'sm' | 'md' | 'lg' = 'md';
  if (size === 'sm') logoSize = 'sm';
  else if (size === 'lg') logoSize = 'lg';

  return (
    <div className="flex justify-center mb-4">
      <AuthLogo size={logoSize} className="animate-pulse" />
    </div>
  );
};

/**
 * Enhanced Loading States Component
 *
 * Provides various animated loading states for authentication flows with:
 * - Smooth transitions and progress indicators
 * - Skeleton loading for different phases
 * - Branded animations with True Intelligence logo
 * - Mobile-optimized responsive design
 * - Full accessibility support
 */
export const EnhancedLoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  submessage,
  progress,
  variant = 'default',
  size = 'md',
}) => {
  const renderLoadingAnimation = () => {
    switch (variant) {
      case 'pulse':
        return <AnimatedDots variant="pulse" />;
      case 'spinner':
        return <AnimatedSpinner size={size} />;
      default:
        return (
          <div className="space-y-4">
            <BrandedLogoAnimation size={size} />
            <AnimatedSpinner size={size} />
          </div>
        );
    }
  };

  return (
    <output
      className="flex flex-col items-center justify-center p-8 text-center"
      aria-live="polite"
      aria-label={message}
    >
      {/* Loading Animation */}
      <div className="mb-6">{renderLoadingAnimation()}</div>

      {/* Progress Bar */}
      {typeof progress === 'number' && (
        <div className="w-full max-w-xs mb-4">
          <ProgressBar progress={progress} />
          <div className="text-xs text-Informational-Text mt-1">
            {Math.round(progress)}% complete
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="space-y-2 max-w-md">
        <h2 className="text-lg font-semibold text-Charcoal">{message}</h2>

        {submessage && (
          <p className="text-sm text-Informational-Text">{submessage}</p>
        )}
      </div>

      {/* Accessibility: Screen reader only content */}
      <div className="sr-only">
        Loading in progress. Please wait while we process your request.
        {typeof progress === 'number' &&
          ` ${Math.round(progress)} percent complete.`}
      </div>
    </output>
  );
};
